'use client';

/**
 * GuideV2: the v2 game-skin guide template (live since 2026-10-09; GuidePageTemplate kept for a revert window).
 * Same inputs as GuidePageTemplate (content, breadcrumbs, fullSlug), same frontmatter, same content: the MDX article is rendered as is and only
 * restyled. Heading ids stay `heading-N` (N = index over the article's h2+h3), the scheme the live template and the HowTo schema use.
 * Desktop >=1100px: sticky chapter rail (active section, reading progress). Phone: sticky Chapters bar that opens a sheet.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { ProcessedContent } from '@/lib/content';
import { getAuthorProfile } from '@/lib/author';
import { nb, splitNumbers, NO_BREAK_STYLE } from '@/lib/nobreak';
import { GUIDE_CSS } from './guide-css';
import { GUIDE_ART_CSS } from './guide-art-css';
import { GUIDE_EXHIBITS_CSS } from './guide-exhibits-css';
import { GUIDE_FLIP_CSS } from './guide-flip-css';
import { GuideHeroArt } from './guide-art';

interface Chapter { id: string; text: string; n: number; subs: { id: string; text: string }[] }
interface Props { content: ProcessedContent; breadcrumbs: Array<{ name: string; url: string }>; fullSlug: string }

const Arrow = () => (
  <svg viewBox="0 0 20 20" aria-hidden fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const Clock = () => (
  <svg viewBox="0 0 20 20" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="10" cy="10" r="7.5" /><path d="M10 5.5V10l3 2" strokeLinecap="round" /></svg>
);
const Chev = () => (
  <svg viewBox="0 0 20 20" aria-hidden fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 8l5 5 5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const LinkedIn = () => (
  <svg viewBox="0 0 24 24" aria-hidden fill="currentColor"><path d="M4.98 3.5a2.5 2.5 0 11-.01 5 2.5 2.5 0 01.01-5zM3 9.75h4v11H3zM9.5 9.75h3.83v1.5h.05c.53-1 1.84-2.06 3.79-2.06 4.05 0 4.8 2.67 4.8 6.13v5.43h-4v-4.82c0-1.15-.02-2.63-1.6-2.63-1.6 0-1.85 1.25-1.85 2.55v4.9h-4z" /></svg>
);
const Close = () => (
  <svg viewBox="0 0 20 20" aria-hidden fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" /></svg>
);


/**
 * Owner rule 2026-10-07: a figure never breaks across lines. The MDX build already wraps every figure in the article text
 * (lib/rehype-nobreak.ts); the legacy exhibits (tables, step lists, charts) print literals from inside their own components, which that
 * pass never sees. Wrap the figures left in those text nodes once, after hydration. Skipped: svg, anything that animates its text
 * (stat cards), and tokens that are already wrapped. Static text only, so React never has a changed string to write back.
 */
function wrapFigures(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const todo: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const tn = n as Text;
    const el = tn.parentElement;
    if (!el || !tn.nodeValue || !/\d/.test(tn.nodeValue)) continue;
    if (el.closest('svg, script, style, .stat-card, [data-gv-nb]') || (el as HTMLElement).style.whiteSpace === 'nowrap') continue;
    todo.push(tn);
  }
  todo.forEach((tn) => {
    const parts = splitNumbers(tn.nodeValue || '');
    if (!parts.some((p) => p.number)) return;
    const frag = document.createDocumentFragment();
    parts.forEach((p) => {
      if (!p.number) { frag.appendChild(document.createTextNode(p.text)); return; }
      const s = document.createElement('span');
      s.setAttribute('data-gv-nb', '');
      s.style.whiteSpace = NO_BREAK_STYLE.whiteSpace;
      s.textContent = p.text;
      frag.appendChild(s);
    });
    tn.parentNode?.replaceChild(frag, tn);
  });
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** 'YYYY-MM-DD...' (or a Date) -> { iso: 'YYYY-MM-DD', text: 'October 14, 2026' }; no locale or time zone, so server and browser agree */
const day = (v: unknown) => {
  const raw = v instanceof Date ? v.toISOString() : typeof v === 'string' ? v : '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  return m ? { iso: `${m[1]}-${m[2]}-${m[3]}`, text: `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` } : null;
};
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const ICONS: Record<string, string> = {
  doc: '<path d="M7 3h7l4 4v14H7z"/><path d="M14 3v4h4M10 12h5M10 16h5"/>',
  search: '<circle cx="11" cy="11" r="6.5"/><path d="M20 20l-4.2-4.2"/>',
  chart: '<path d="M4 20V11M10 20V5M16 20v-6M3 20h18"/>',
  target: '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4.5"/><path d="M12 12h.01"/>',
  bolt: '<path d="M13 2.5L4.5 14H11l-1 7.5L19 10h-6.5z"/>',
  users: '<circle cx="9" cy="8.5" r="3.5"/><path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6M16 5a3.5 3.5 0 010 7M21.5 20c0-2.8-1.6-4.7-4-5.6"/>',
  shield: '<path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="M9 12l2 2 4-4"/>',
  flag: '<path d="M5 21V4M5 4h11l-2 4 2 4H5"/>',
  layers: '<path d="M12 3l9 5-9 5-9-5zM3 13l9 5 9-5"/>',
  loop: '<path d="M20 12a8 8 0 01-14 5.3M4 12a8 8 0 0114-5.3"/><path d="M18 3v4h-4M6 21v-4h4"/>',
};
const ICON_KEYS = Object.keys(ICONS);
const ICON_RULES: Array<[RegExp, string]> = [
  [/what is|before you start|intro|reality/i, 'doc'],
  [/look for|choose|compare|comparison|which|decid/i, 'search'],
  [/cost|roi|math|number|pric|compound|timeline/i, 'chart'],
  [/ai\b|speed|automat|future|trend|optimi/i, 'bolt'],
  [/team|agent|partner|referral|people|loan officer|pattern/i, 'users'],
  [/compliance|risk|verified|gap|limit/i, 'shield'],
  [/conclusion|final|ready|making it real|bottom line/i, 'flag'],
  [/strateg|playbook|lead|channel|foundation|engine|choose your/i, 'target'],
  [/fit|integrat|system|stack/i, 'layers'],
];
const pickIcon = (title: string, i: number) => ICONS[(ICON_RULES.find(([r]) => r.test(title)) || [])[1] as string] || ICONS[ICON_KEYS[i % ICON_KEYS.length]];
const FIGURE = /^[~≈]?[$]?\d[\d,.]*\s?(?:%|x|X|[kKmMbB]\+?|\+)?(?:\s?(?:to|[-–—])\s?[$]?\d[\d,.]*\s?(?:%|x|[kKmMbB]\+?|\+)?)?$/;
const CallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><rect x="3" y="4.5" width="18" height="16" rx="3" /><path d="M8 2.5v4M16 2.5v4M3 10h18M9 15l2 2 4-4" /></svg>
);
// the strategy-call block copy (src/components/v2/roi/dialogs/dialogs-strings.ts D.ctaLine) and the demo form, as used everywhere else on the site
const RAIL_LINE = 'The call is where we work together to develop a customized growth strategy for your business.';
const RAIL_HREF = '/mortgage-lead-generation-demo';
const OFFSET = () => (window.innerWidth >= 1100 ? 92 : 140);

export function GuideV2({ content, breadcrumbs, fullSlug }: Props) {
  const { meta } = content;
  const articleRef = useRef<HTMLElement>(null);
  const progRef = useRef<HTMLElement>(null);
  const railBarRef = useRef<HTMLElement>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [active, setActive] = useState(0); // index into chapters
  const [activeSub, setActiveSub] = useState('');
  const [pct, setPct] = useState(0);
  const [sheet, setSheet] = useState(false);
  const [resume, setResume] = useState<number | null>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const sheetBtnRef = useRef<HTMLButtonElement>(null);
  const sheetCloseRef = useRef<HTMLButtonElement>(null);
  const bookmarkKey = `guideProgress:${fullSlug}`;

  const cfg = (meta.guideConfig || {}) as { totalSections?: number; estimatedReadingTime?: number; difficulty?: string; chapterTitles?: string[]; dataAsOf?: string };
  const minutes = cfg.estimatedReadingTime || Math.max(1, Math.ceil((content.wordCount || 0) / 200));
  const difficulty = cfg.difficulty || 'intermediate';
  const sections = cfg.totalSections || chapters.length;
  const h1 = meta.h1 || meta.title || 'Untitled Guide';
  const accent = (meta as { h1Accent?: string }).h1Accent;
  const profile = getAuthorProfile(meta.author);
  const faqs = meta.faqs && meta.faqs.length > 0 ? meta.faqs : null;
  const related = meta.relatedPages && meta.relatedPages.length > 0 ? meta.relatedPages : null;
  // visible dates (the same ones the Article schema carries); "Updated" only when it differs from the publish date
  const published = day(meta.publishedTime);
  const modified = day(meta.modifiedTime);
  const updated = modified && (!published || modified.iso !== published.iso) ? modified : null;
  const dataAsOf = day(cfg.dataAsOf);
  // the last breadcrumb is built from the URL slug; a guide can name it properly (frontmatter breadcrumbTitle)
  const crumb = (meta as { breadcrumbTitle?: string }).breadcrumbTitle;

  /* ---- headings: ids (heading-N, as the live template), chapters, table frames */
  useEffect(() => {
    const art = articleRef.current;
    if (!art) return;
    const hs = Array.from(art.querySelectorAll('h2, h3'));
    hs.forEach((h, i) => { h.id = `heading-${i}`; });
    const list: Chapter[] = [];
    hs.forEach((h) => {
      const isCta = !!h.closest('section.not-prose'); // a CtaBlock headline is an h2 but not a chapter
      if (h.tagName === 'H2') {
        if (isCta) return;
        list.push({ id: h.id, text: h.textContent || '', n: list.length + 1, subs: [] });
        h.setAttribute('data-n', String(list.length).padStart(2, '0'));
      } else if (list.length && !isCta) {
        list[list.length - 1].subs.push({ id: h.id, text: h.textContent || '' });
      }
    });
    if (faqs) list.push({ id: 'gv-faq', text: 'Frequently Asked Questions', n: list.length + 1, subs: [] });
    setChapters(list);
    // wide tables scroll inside a frame instead of pushing the page sideways; a fade + chip shows when there is more to the side
    const frames: Array<() => void> = [];
    art.querySelectorAll('table').forEach((t) => {
      if (t.parentElement?.classList.contains('gv-tw') || t.closest('.overflow-x-auto')) return; // a legacy exhibit already scrolls itself
      const w = document.createElement('div');
      w.className = 'gv-tw';
      w.setAttribute('role', 'region');
      w.setAttribute('tabindex', '0');
      w.setAttribute('aria-label', 'Table, scroll sideways on a small screen');
      t.parentNode?.insertBefore(w, t);
      w.appendChild(t);
      const f = document.createElement('div');
      f.className = 'gv-twf';
      if (t.rows.length > 11) f.setAttribute('data-long', '1');
      w.parentNode?.insertBefore(f, w);
      f.appendChild(w);
      const chip = document.createElement('span');
      chip.className = 'gv-swipe';
      chip.setAttribute('aria-hidden', 'true');
      chip.innerHTML = 'Scroll <svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 10h11M11 5l5 5-5 5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      f.appendChild(chip);
      const upd = () => {
        const m: string[] = [];
        if (w.scrollLeft > 4) m.push('l');
        if (w.scrollLeft + w.clientWidth < w.scrollWidth - 4) m.push('r');
        f.setAttribute('data-more', m.join(' '));
      };
      w.addEventListener('scroll', upd, { passive: true });
      frames.push(upd);
      // phone: each row becomes a card, so give every cell its column label (CSS prints it); no sideways scroll
      const heads = Array.from(t.rows[0]?.cells || []).map((c) => (c.textContent || '').trim());
      Array.from(t.tBodies).forEach((tb) => Array.from(tb.rows).forEach((r) => Array.from(r.cells).forEach((c, i) => c.setAttribute('data-label', heads[i] || ''))));
    });
    const updFrames = () => frames.forEach((f) => f());
    updFrames();
    window.addEventListener('resize', updFrames);
    const t1 = setTimeout(updFrames, 800);

    // chapter openers: an icon chip in each h2 and a divider between chapters; strategy and review headings get a number badge
    let reviewN = 0; let chapterTitle = '';
    const decorate = Array.from(art.querySelectorAll('h2, h3'));
    let chIdx = -1;
    decorate.forEach((h) => {
      if (h.closest('section.not-prose')) return;
      const text = h.textContent || '';
      if (h.tagName === 'H2') {
        chIdx++;
        chapterTitle = text; reviewN = 0;
        if (!h.querySelector('.gv-ic')) {
          const ic = document.createElement('span');
          ic.className = 'gv-ic';
          ic.setAttribute('aria-hidden', 'true');
          ic.innerHTML = `<svg viewBox="0 0 24 24">${pickIcon(text, chIdx)}</svg>`;
          h.insertBefore(ic, h.firstChild);
        }
        const prev = h.previousElementSibling;
        if (chIdx > 0 && !(prev && prev.classList.contains('gv-div'))) {
          const d = document.createElement('div');
          d.className = 'gv-div';
          d.setAttribute('aria-hidden', 'true');
          d.innerHTML = '<i></i><b></b><i></i>';
          h.parentNode?.insertBefore(d, h);
        }
      } else {
        const m = /^Strategy (\d+)\b/.exec(text);
        if (m) { h.classList.add('gv-num'); h.setAttribute('data-num', m[1]); }
        else if (/in-depth/i.test(chapterTitle) && !/^sources$/i.test(text.trim())) { reviewN++; h.classList.add('gv-num'); h.setAttribute('data-num', String(reviewN)); }
      }
    });
    // key takeaway panels ("Bottom line:") and figure-led stat callouts
    art.querySelectorAll('p').forEach((p) => {
      if (p.closest('.mdx-component-base, .component-body, section.not-prose, .stat-card')) return;
      const tx = (p.textContent || '').trim();
      if (/^(the )?bottom line\b/i.test(tx)) p.classList.add('gv-take');
    });
    art.querySelectorAll('p, li').forEach((el) => {
      const f = el.firstElementChild;
      if (!f || f.tagName !== 'STRONG' || el.closest('.mdx-component-base, .component-body, section.not-prose, .stat-card, table')) return;
      const lead = (f.textContent || '').trim();
      const rest = (el.textContent || '').trim().slice(lead.length).trim();
      if (FIGURE.test(lead) && rest.length > 12) el.classList.add('gv-stat');
    });
    wrapFigures(art);
    // a link with #heading-N or #gv-faq opens at the right place once the ids exist
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) setTimeout(() => document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'instant' as ScrollBehavior }), 60);
    return () => { window.removeEventListener('resize', updFrames); clearTimeout(t1); };
  }, [faqs]);

  /* ---- scroll: progress (transform only), active chapter, saved position */
  useEffect(() => {
    let raf = 0;
    let lastPct = -1;
    let saveT: ReturnType<typeof setTimeout> | undefined;
    const update = () => {
      raf = 0;
      const art = articleRef.current;
      if (!art) return;
      const r = art.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = Math.max(1, r.height - vh * 0.4);
      const p = Math.min(1, Math.max(0, (vh * 0.2 - r.top) / total));
      if (progRef.current) progRef.current.style.transform = `scaleX(${p})`;
      if (railBarRef.current) railBarRef.current.style.transform = `scaleX(${p})`;
      const ip = Math.round(p * 100);
      if (ip !== lastPct) { lastPct = ip; setPct(ip); }
      // active chapter and sub-section: the last heading whose top has passed the line under the sticky header(s)
      const line = window.innerWidth >= 1100 ? 160 : 190;
      let cur = 0; let sub = ''; let idx = -1;
      art.querySelectorAll('h2, h3').forEach((h) => {
        if (h.closest('section.not-prose')) return;
        if (h.tagName === 'H2') idx++;
        if (h.getBoundingClientRect().top <= line) {
          if (h.tagName === 'H2') { cur = idx; sub = ''; } else sub = h.id;
        }
      });
      setActiveSub(sub);
      const band = document.querySelector('.gv-close');
      if (ctaRef.current) ctaRef.current.setAttribute('data-hide', band && band.getBoundingClientRect().top < vh - 60 ? '1' : '0');
      const faq = document.getElementById('gv-faq');
      const inFaq = !!faq && faq.getBoundingClientRect().top <= line;
      setActive(inFaq ? -1 : cur);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
      clearTimeout(saveT);
      saveT = setTimeout(() => {
        try {
          if (window.scrollY > 300) localStorage.setItem(bookmarkKey, String(Math.round(window.scrollY)));
        } catch { /* storage blocked: nothing to save */ }
      }, 1000);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); clearTimeout(saveT); };
  }, [bookmarkKey, chapters.length]);

  /* ---- resume where you left off (same storage key as the live guide) */
  useEffect(() => {
    try {
      if (window.location.hash) return;
      const saved = parseInt(localStorage.getItem(bookmarkKey) || '', 10);
      if (saved > 600) { setResume(saved); const t = setTimeout(() => setResume(null), 8000); return () => clearTimeout(t); }
    } catch { /* storage blocked */ }
  }, [bookmarkKey]);

  /* ---- phone sheet: lock page scroll, Esc closes, focus returns to the bar */
  useEffect(() => {
    if (!sheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheet(false); };
    window.addEventListener('keydown', onKey);
    sheetCloseRef.current?.focus({ preventScroll: true });
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); sheetBtnRef.current?.focus({ preventScroll: true }); };
  }, [sheet]);

  const pending = useRef<string | null>(null);
  const anim = useRef(0);
  /** Eased scroll to a heading: the target is re-read every frame (late layout cannot cancel it), the user can interrupt it, reduced motion jumps. */
  const jump = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const targetY = () => Math.max(0, Math.min(document.documentElement.scrollHeight - window.innerHeight, el.getBoundingClientRect().top + window.scrollY - OFFSET()));
    const finish = () => {
      try { history.replaceState(null, '', `#${id}`); } catch { /* ignore */ }
      el.setAttribute('tabindex', '-1');
      el.focus({ preventScroll: true });
    };
    cancelAnimationFrame(anim.current);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { window.scrollTo({ top: targetY(), behavior: 'instant' as ScrollBehavior }); finish(); return; }
    const from = window.scrollY;
    const dist = Math.abs(targetY() - from);
    const dur = Math.min(1100, Math.max(420, 360 + dist * 0.16));
    const t0 = performance.now();
    const stop = () => { cancelAnimationFrame(anim.current); off(); };
    const off = () => ['wheel', 'touchstart', 'keydown'].forEach((e) => window.removeEventListener(e, stop));
    ['wheel', 'touchstart', 'keydown'].forEach((e) => window.addEventListener(e, stop, { passive: true }));
    const tick = (now: number) => {
      const t = Math.min(1, (now - t0) / dur);
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      window.scrollTo({ top: from + (targetY() - from) * e, behavior: 'instant' as ScrollBehavior });
      if (t < 1) anim.current = requestAnimationFrame(tick);
      else { off(); finish(); }
    };
    anim.current = requestAnimationFrame(tick);
  }, []);
  const go = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    if (sheet) { pending.current = id; setSheet(false); } else jump(id); // the open sheet locks the page scroll: jump once it has closed
  }, [sheet, jump]);
  useEffect(() => {
    if (sheet || !pending.current) return;
    const id = pending.current;
    pending.current = null;
    requestAnimationFrame(() => jump(id));
  }, [sheet, jump]);

  // keep the active chapter in view inside the (scrolling) rail without moving the page
  useEffect(() => {
    const list = document.querySelector<HTMLElement>('.gv-rail .gv-rail-l');
    const cur = list?.querySelector<HTMLElement>('.gv-ch[aria-current="true"]');
    if (!list || !cur) return;
    const top = cur.offsetTop - list.offsetTop;
    if (top < list.scrollTop + 8 || top + cur.offsetHeight > list.scrollTop + list.clientHeight - 8) list.scrollTop = Math.max(0, top - 24);
  }, [active, chapters.length]);

  const left = Math.max(0, Math.ceil(minutes * (1 - pct / 100)));
  const curTitle = active >= 0 ? chapters[active]?.text : 'Frequently Asked Questions';

  const rail = (inSheet: boolean) => (
    <>
      {!inSheet && (
        <div className="gv-rail-h">
          <p className="gv-rail-t">Chapters</p>
          <div className="gv-rail-m"><span><b>{pct}%</b> read</span><span><b>{left}</b> min left</span></div>
          <div className="gv-rail-bar" aria-hidden><i ref={railBarRef as React.RefObject<HTMLElement>} /></div>
        </div>
      )}
      <ol className="gv-rail-l">
        {chapters.map((c, i) => {
          const on = active === i || (active === -1 && c.id === 'gv-faq');
          return (
            <li key={c.id}>
              <a className="gv-ch" href={`#${c.id}`} aria-current={on ? 'true' : undefined} onClick={(e) => go(e, c.id)}>
                <span className="n">{pad2(c.n)}</span><span>{nb(c.text)}</span>
              </a>
              {on && !inSheet && c.subs.length > 0 && (
                <ul className="gv-subs">
                  {c.subs.map((s) => (
                    <li key={s.id}><a className="gv-sub-a" href={`#${s.id}`} aria-current={activeSub === s.id ? 'true' : undefined} onClick={(e) => go(e, s.id)}>{nb(s.text)}</a></li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
    </>
  );

  // H1 with the accent words in the gradient (only when the frontmatter names an accent that is part of the H1)
  const h1Node = (() => {
    const i = accent ? h1.indexOf(accent) : -1;
    if (!accent || i < 0) return <>{nb(h1)}</>;
    return <>{nb(h1.slice(0, i))}<span className="gv-grad">{nb(accent)}</span>{nb(h1.slice(i + accent.length))}</>;
  })();

  // "By Name, title" in the sub-headline is the credit: it sits on the author row, the rest is the sub copy
  const subLines = meta.subHeadline ? meta.subHeadline.split('\n').filter((l) => l.trim()) : [];
  let credit = '';
  const subCopy: string[] = [];
  subLines.forEach((l) => {
    const m = /(?:^|\s)(By [A-Z][\w.'-]+ [A-Z][\w.'-]+.*)$/.exec(l);
    if (m && !credit) { credit = m[1]; const rest = l.slice(0, l.length - m[1].length).trim(); if (rest) subCopy.push(rest); } else subCopy.push(l);
  });
  const creditNode = (() => {
    const nm = profile?.name;
    const i = nm ? credit.indexOf(nm) : -1;
    if (!nm || i < 0) return <>{nb(credit)}</>;
    return <>{nb(credit.slice(0, i))}<b>{nm}</b>{nb(credit.slice(i + nm.length))}</>;
  })();

  return (
    <div className="gv2">
      <style dangerouslySetInnerHTML={{ __html: GUIDE_CSS + GUIDE_ART_CSS + GUIDE_EXHIBITS_CSS + GUIDE_FLIP_CSS }} />
      <div className="gv-prog" aria-hidden><i ref={progRef as React.RefObject<HTMLElement>} /></div>

      <header className="gv-hero" id="lp-hero">
        <div className="gv-hero-in">
          <nav className="gv-crumbs" aria-label="Breadcrumb">
            <ol>
              <li><Link href="/">Home</Link></li>
              {breadcrumbs.map((b, i) => (
                <li key={b.url}>{i === breadcrumbs.length - 1 ? <span aria-current="page">{crumb || b.name}</span> : <Link href={b.url}>{b.name}</Link>}</li>
              ))}
            </ol>
          </nav>
          <div className="gv-hero-g">
            <div className="gv-hero-t">
              <div className="gv-chips">
                <span className="gv-chip gv-chip--free">FREE GUIDE</span>
                <span className="gv-chip">{cap(difficulty)}</span>
                <span className="gv-chip"><Clock />{nb(`${minutes} min read`)}</span>
                {sections ? <span className="gv-chip">{nb(`${sections} sections`)}</span> : null}
              </div>
              <h1 className="gv-h1">{h1Node}</h1>
              {subCopy.length > 0 && (
                <div className="gv-sub">{subCopy.map((l, i) => <p key={i}>{nb(l)}</p>)}</div>
              )}
              {(profile || credit) && (
                <div className="gv-byrow">
                  <a className="gv-by" href={profile?.pagePath || '#'}>
                    {profile && <Image src={profile.image} alt={profile.name} width={44} height={44} priority />}
                    <span className="gv-cred">{credit ? creditNode : <><b>{profile?.name}</b> · {profile?.title}</>}</span>
                  </a>
                  {profile?.linkedin && (
                    <a className="gv-li" href={profile.linkedin} target="_blank" rel="author noopener noreferrer" aria-label={`${profile.name} on LinkedIn`}><LinkedIn /></a>
                  )}
                </div>
              )}
              {published && (
                <p className="gv-dates">
                  <span>Published <time dateTime={published.iso}>{nb(published.text)}</time></span>
                  {updated && <span>Updated <time dateTime={updated.iso}>{nb(updated.text)}</time></span>}
                  {dataAsOf && <span>Data pulled <time dateTime={dataAsOf.iso}>{nb(dataAsOf.text)}</time></span>}
                </p>
              )}
            </div>
            <div className="gv-hero-art"><GuideHeroArt slug={fullSlug.replace(/^guide\//, '')} /></div>
          </div>
        </div>
      </header>

      {/* phone: compact sticky chapters bar */}
      <div className="gv-pbar">
        <button ref={sheetBtnRef} type="button" aria-haspopup="dialog" aria-expanded={sheet} onClick={() => setSheet(true)}>
          <span className="lab">Chapters</span>
          <span className="cur">{nb(curTitle || '')}</span>
          <span className="pct">{pct}%</span>
          <Chev />
        </button>
      </div>
      <div className="gv-sheet" data-open={sheet} role="dialog" aria-modal="true" aria-label="Chapters" aria-hidden={!sheet}>
        <div className="gv-sheet-bg" onClick={() => setSheet(false)} />
        <div className="gv-sheet-p">
          <div className="gv-sheet-h">
            <h2>Chapters <span style={{ color: 'var(--g-mut)', fontWeight: 600, fontSize: 14 }}>{nb(`${left} min left`)}</span></h2>
            <button ref={sheetCloseRef} type="button" className="gv-sheet-x" aria-label="Close chapters" onClick={() => setSheet(false)}><Close /></button>
          </div>
          {sheet && rail(true)}
          <div className="gv-sheet-cta">
            <div className="gv-cta-row"><span className="gv-cta-ico"><CallIcon /></span><p className="gv-cta-txt">{nb(RAIL_LINE)}</p></div>
            <a className="gv-call" href={RAIL_HREF} data-cta="guide-sheet"><span>Book a Strategy Call</span><Arrow /></a>
          </div>
        </div>
      </div>

      <div className="gv-wrap">
        <div className="gv-grid">
          <aside className="gv-rail" aria-label="Guide chapters">
            <nav className="gv-rail-in" aria-label="Chapters">
              {chapters.length > 0 ? rail(false) : null}
              <div className="gv-rail-cta" ref={ctaRef}>
                <div className="gv-cta-row"><span className="gv-cta-ico"><CallIcon /></span><p className="gv-cta-txt">{nb(RAIL_LINE)}</p></div>
                <a className="gv-call" href={RAIL_HREF} data-cta="guide-rail"><span>Book a Strategy Call</span><Arrow /></a>
              </div>
            </nav>
          </aside>

          <main className="gv-main">
            <article ref={articleRef} className="gv-art guide-content">
              {content.compiledContent ? <div>{content.compiledContent}</div> : <p>Content is being processed. Please refresh the page.</p>}
            </article>

            {profile && (
              <aside className="gv-author" aria-label="About the author">
                <Image src={profile.image} alt={profile.name} width={72} height={72} />
                <div>
                  <span className="k">Written by</span>
                  <b>{profile.name}</b>
                  <div className="t">{profile.title}</div>
                  <p>{nb(profile.bioShort)}</p>
                  <div className="gv-author-l">
                    <a href={profile.pagePath}>More about {profile.name}</a>
                    {profile.linkedin && <a href={profile.linkedin} target="_blank" rel="author noopener noreferrer"><LinkedIn />LinkedIn</a>}
                  </div>
                </div>
              </aside>
            )}

            {faqs && (
              <section className="gv-sec" aria-labelledby="gv-faq">
                <h2 className="gv-sec-h" id="gv-faq">Frequently Asked Questions</h2>
                <div className="gv-faq">
                  {faqs.map((f, i) => (
                    <details key={i} open={i === 0}>
                      <summary><h3>{nb(f.question)}</h3></summary>
                      <div className="a">{nb(f.answer)}</div>
                    </details>
                  ))}
                </div>
              </section>
            )}

            {related && (
              <section className="gv-sec" aria-labelledby="gv-related">
                <h2 className="gv-sec-h" id="gv-related">Related Resources</h2>
                <div className="gv-rel">
                  {related.map((p, i) => (
                    <Link key={i} href={p.href}>
                      <h3>{nb(p.title)}</h3>
                      <p>Learn more about {nb(p.anchor)}</p>
                      <span className="go">Read More <Arrow /></span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </main>
        </div>
      </div>

      {meta.heroCtaText && meta.heroCtaLink && (
        <section className="gv-close" aria-labelledby="gv-close-h">
          <div className="gv-close-in">
            <h2 id="gv-close-h">Ready to Generate <span className="gv-grad">Your Own</span> Mortgage Leads?</h2>
            <p>Stop reading about it. Start doing it with LeadPops.</p>
            <a className="gv-cta" href={meta.heroCtaLink} data-cta="guide-close">{nb(meta.heroCtaText)}<Arrow /></a>
            {meta.heroCtaSubtext && <small>{nb(meta.heroCtaSubtext)}</small>}
          </div>
        </section>
      )}

      {resume !== null && (
        <div className="gv-toast" role="status">
          <p>Welcome back! Continue where you left off?</p>
          <div>
            <button type="button" className="gv-spec" onClick={() => { window.scrollTo({ top: resume, behavior: 'smooth' }); setResume(null); }}>Continue Reading</button>
            <button type="button" onClick={() => { try { localStorage.removeItem(bookmarkKey); } catch { /* storage blocked */ } window.scrollTo({ top: 0, behavior: 'smooth' }); setResume(null); }}>Start from Top</button>
          </div>
        </div>
      )}
    </div>
  );
}
